/**
 * Every decision `/jobs` makes, and every mutation it APPLIES.
 *
 * The same split as lib/startView.ts, lib/masterDataView.ts, lib/sheetView.ts
 * and lib/ebayView.ts, for the same reason (the STANDING RULE carried through
 * the Task 7-12 reviews): pinning a DECISION is not enough, because every
 * escape those reviews found was planted at a CALL SITE. So two layers live
 * here and views/Jobs.tsx computes nothing.
 *
 *  1. THE DECISIONS: which eight actions exist and how each is gated, what a
 *     409 means, how a DATEV 400 differs from a DATEV 502, when the monitor is
 *     allowed to poll, and which of the four metric states a bundle is in.
 *     Pure, driven exhaustively from Node (test/jobsView.test.ts).
 *  2. THE ORCHESTRATION: `*Action`-shaped functions taking their collaborators
 *     as parameters, so the code that decides WHICH ACTION IS SENT -- the risk
 *     this task actually carries -- is callable with no renderer.
 *
 * ── THE THREE THINGS THIS FILE EXISTS TO MAKE UNBYPASSABLE ──────────────────
 *
 *  A. AN ACTION RUNS AGAINST THE ACTION THE OPERATOR PICKED. `ADMIN_ACTIONS` is
 *     keyed by a closed union and `runAdminAction` sends `name` itself; there is
 *     no second place where an action string is spelled, so a call site cannot
 *     wire the wrong one without the table disagreeing.
 *  B. A RUN-NOW FIRES ONCE. The re-entrancy gate is a MUTABLE OBJECT checked
 *     and set SYNCHRONOUSLY here, before the first `await` -- not a `disabled`
 *     attribute and not React state, both of which are still stale on the
 *     second click of a double-click (they update on the next render, which has
 *     not happened yet). A second click inside the same turn sends nothing.
 *  C. A DECLINED CONFIRMATION SENDS NOTHING. `spec.confirm` is consulted HERE,
 *     so the two expensive/irreversible actions cannot be run past a `Nein` by
 *     a JSX refactor that forgets to ask.
 *
 * ── AND THE THING IT EXISTS TO STOP MISREPORTING ────────────────────────────
 * An `inline` action's RESPONSE IS THE FINAL STATUS (admin spec 4.4): the
 * server reads the jobs row back and reports it (adminRoutes.ts:1675-1684). So
 * success is `status !== 'error'`, never "the promise resolved" -- a
 * status-blind success path here paints a failed job green, which is the
 * controller's own example of the failure class this view must not have.
 */
import type { QueryClient } from '@tanstack/react-query';
import { ApiError } from './api';
import { gateReason, isAllowed, type CapabilityCtx, type GateReason, type Surface } from './capabilities';
import type { ActionGate } from './gate';
import { t } from './i18n';
import { readNumber, METRIC_SERIES, seriesRowsOf } from './metrics';
import { errorMessage } from '../components/ErrorNotice';
import { invalidateMutation, reportAuthFailure, reportAuthStatus } from '../queries';
import type { MetricRange, MetricsBundle } from './types';

// ── the eight actions (D7), mirrored ─────────────────────────────────────────

/**
 * `ADMIN_ACTIONS` in api/src/adminRoutes.ts:1323-1359 is the server's registry;
 * this is the client's half of it, and the two are compared row by row in
 * test/jobsView.test.ts's table. The ORDER is the render order.
 */
export const ACTION_NAMES = [
  'refresh-cache',
  'import-master-data',
  'ebay-sync',
  'refresh-listings',
  'update-skus',
  'ebay-review-refresh',
  'ebay-publish',
  'price-scan',
] as const;
export type ActionName = (typeof ACTION_NAMES)[number];

export const ACTION_GROUPS = ['maintenance', 'ebay', 'priceScan'] as const;
export type ActionGroup = (typeof ACTION_GROUPS)[number];

export interface ActionSpec {
  /** jobs.type, i.e. what the monitor's Typ column shows for this action's row. */
  jobType: string;
  /**
   * 'inline'     — the response carries the FINAL status; render it.
   * 'background' —202 { jobId, status: 'running' }; say "gestartet" and let the
   *                5 s poll take over.
   */
  mode: 'inline' | 'background';
  /** The capabilities.ts surface that gates the button. `null` = ungated. */
  surface: Surface | null;
  group: ActionGroup;
  /**
   * Whether pressing the button asks first. TWO of the eight do, and both are
   * chosen for consequence rather than for being scary-sounding:
   *  - `ebay-publish` puts drafts LIVE on eBay under the tenant's account
   *    (api/src/ebayReviewPublish.js) -- visible to buyers, and unpublishing is
   *    a separate manual pass.
   *  - `import-master-data` overwrites D1 master data from the sheet
   *    (adminRoutes.ts:1352-1358); it exists for the phase-6 cutover and is not
   *    something to press to see what happens.
   * The other six are re-runnable passes whose worst case is a wasted quota
   * slice, and a confirmation on all eight is a confirmation nobody reads.
   */
  confirm: boolean;
}

export const ADMIN_ACTIONS: Record<ActionName, ActionSpec> = {
  'refresh-cache': { jobType: 'refresh_cache', mode: 'inline', surface: null, group: 'maintenance', confirm: false },
  'import-master-data': { jobType: 'import_master_data', mode: 'inline', surface: null, group: 'maintenance', confirm: true },
  'ebay-sync': { jobType: 'ebay_sync', mode: 'background', surface: 'ebayActions', group: 'ebay', confirm: false },
  'refresh-listings': { jobType: 'ebay_listings', mode: 'background', surface: 'ebayActions', group: 'ebay', confirm: false },
  'update-skus': { jobType: 'update_skus', mode: 'background', surface: 'ebayActions', group: 'ebay', confirm: false },
  'ebay-review-refresh': { jobType: 'ebay_review_refresh', mode: 'background', surface: 'ebayActions', group: 'ebay', confirm: false },
  'ebay-publish': { jobType: 'ebay_publish', mode: 'background', surface: 'ebayActions', group: 'ebay', confirm: true },
  'price-scan': { jobType: 'price_scan', mode: 'background', surface: 'priceScanAction', group: 'priceScan', confirm: false },
};

/** Literal `t()` calls, one per action, for the reason Blocking.tsx's `copyFor`
 *  gives: a computed key still renders, but test/i18nKeys.test.ts could no
 *  longer prove the copy exists, and a typo would ship as a visible key. */
export function actionLabel(name: ActionName): string {
  switch (name) {
    case 'refresh-cache': return t('jobs.action.refreshCache');
    case 'import-master-data': return t('jobs.action.importMasterData');
    case 'ebay-sync': return t('jobs.action.ebaySync');
    case 'refresh-listings': return t('jobs.action.refreshListings');
    case 'update-skus': return t('jobs.action.updateSkus');
    case 'ebay-review-refresh': return t('jobs.action.ebayReviewRefresh');
    case 'ebay-publish': return t('jobs.action.ebayPublish');
    case 'price-scan': return t('jobs.action.priceScan');
  }
}

export function actionConfirmText(name: ActionName): string {
  switch (name) {
    case 'ebay-publish': return t('jobs.action.ebayPublish.confirm');
    case 'import-master-data': return t('jobs.action.importMasterData.confirm');
    default: return '';
  }
}

export function actionGroupLabel(group: ActionGroup): string {
  switch (group) {
    case 'maintenance': return t('jobs.actions.group.maintenance');
    case 'ebay': return t('jobs.actions.group.ebay');
    case 'priceScan': return t('jobs.actions.group.priceScan');
  }
}

/**
 * capabilities.ts is CONSUMED, never re-derived (this package's rule). An
 * ungated action answers 'ok' unconditionally -- the two inline ones are gated
 * on nothing but the manager role the whole route already needs.
 */
export function actionGateReason(name: ActionName, ctx: CapabilityCtx): GateReason {
  const { surface } = ADMIN_ACTIONS[name];
  return surface === null ? 'ok' : gateReason(surface, ctx);
}

export function visibleActions(group: ActionGroup, ctx: CapabilityCtx): ActionName[] {
  return ACTION_NAMES.filter((name) => {
    const spec = ADMIN_ACTIONS[name];
    return spec.group === group && (spec.surface === null || isAllowed(spec.surface, ctx));
  });
}

// ── what came back ───────────────────────────────────────────────────────────

/** `POST /admin-api/actions/:action`, both modes (adminRoutes.ts:1665-1684).
 *  The twin of lib/startView.ts's `RefreshCacheResponse`, which describes the
 *  same body for the one action the Start view runs. */
export interface AdminActionResponse {
  jobId: string;
  status: string;
  error?: string | null;
  stats?: unknown;
}

export type ActionOutcome =
  /** inline, and the row says done. */
  | { kind: 'inlineDone'; name: ActionName; jobId: string }
  /** inline, and the row says error. The row was still written. */
  | { kind: 'inlineFailed'; name: ActionName; jobId: string; error: string | null }
  /** background: 202, the row is 'running', the poll takes it from here. */
  | { kind: 'started'; name: ActionName; jobId: string }
  /** 409 job_running — the SAME type is already in flight; the body names it. */
  | { kind: 'jobRunning'; name: ActionName; jobId: string | null }
  /** 409 tenant_busy — a DIFFERENT job holds the per-tenant lock. */
  | { kind: 'tenantBusy'; name: ActionName; runningType: string }
  /** 503 feature_disabled — unreachable through a gated button, so: a bug. */
  | { kind: 'featureDisabledBug'; name: ActionName; feature: string }
  | { kind: 'failed'; name: ActionName; message: string };

export type OutcomeTone = 'ok' | 'error' | 'bug';

export function actionOutcomeTone(outcome: ActionOutcome): OutcomeTone {
  switch (outcome.kind) {
    case 'inlineDone':
    case 'started':
      return 'ok';
    case 'featureDisabledBug':
      return 'bug';
    case 'inlineFailed':
    case 'jobRunning':
    case 'tenantBusy':
    case 'failed':
      return 'error';
  }
}

export function actionOutcomeMessage(outcome: ActionOutcome): string {
  switch (outcome.kind) {
    case 'inlineDone':
      return t('jobs.outcome.done', { action: actionLabel(outcome.name) });
    case 'inlineFailed':
      return outcome.error
        ? t('jobs.outcome.failedWithError', { action: actionLabel(outcome.name), error: outcome.error })
        : t('jobs.outcome.failed', { action: actionLabel(outcome.name) });
    case 'started':
      return t('jobs.outcome.started', { action: actionLabel(outcome.name) });
    case 'jobRunning':
      return t('error.job_running');
    case 'tenantBusy':
      return t('error.tenant_busy', { type: outcome.runningType });
    case 'featureDisabledBug':
      return t('jobs.outcome.featureDisabledBug', { feature: outcome.feature });
    case 'failed':
      return outcome.message;
  }
}

const readString = (value: unknown): string | null =>
  typeof value === 'string' && value !== '' ? value : null;

/**
 * The three conflict/refusal codes this route answers, classified by CODE and
 * not by status alone: `feature_disabled` is a 503 that must NOT read as the
 * ordinary "nicht eingerichtet" state, and the two 409s carry different
 * payloads and different repairs.
 */
export function classifyActionFailure(name: ActionName, error: unknown): ActionOutcome {
  if (error instanceof ApiError) {
    if (error.code === 'job_running') {
      return { kind: 'jobRunning', name, jobId: readString(error.body.jobId) };
    }
    if (error.code === 'tenant_busy') {
      // The payload names the OTHER job, which is the only useful thing to say.
      return { kind: 'tenantBusy', name, runningType: readString(error.body.runningType) ?? '' };
    }
    if (error.code === 'feature_disabled') {
      return { kind: 'featureDisabledBug', name, feature: readString(error.body.feature) ?? '' };
    }
  }
  return { kind: 'failed', name, message: errorMessage(error) };
}

/**
 * The RESOLVED response, read for what it actually says.
 *
 * `inline` is the dangerous half: the promise resolving means the REQUEST
 * succeeded, and the job inside it may still have failed. `status === 'error'`
 * is the server's own word for that (it read the row back), and the server
 * defaults an unreadable row to `'error'` rather than to `'done'` -- so this
 * treats anything that is not literally 'error' as done, and never the reverse.
 */
export function classifyActionResponse(name: ActionName, response: AdminActionResponse): ActionOutcome {
  const jobId = readString(response.jobId) ?? '';
  if (ADMIN_ACTIONS[name].mode === 'background') return { kind: 'started', name, jobId };
  if (response.status === 'error') {
    return { kind: 'inlineFailed', name, jobId, error: response.error ?? null };
  }
  return { kind: 'inlineDone', name, jobId };
}

// ── the orchestration ────────────────────────────────────────────────────────

/**
 * The re-entrancy gate, and it is a MUTABLE OBJECT on purpose (a `useRef` at
 * the call site). React state cannot do this job: `setPending(name)` schedules
 * a render, and the second click of a double-click is dispatched before that
 * render happens, so a `disabled` attribute and a `busy` boolean are both still
 * false when it arrives. Reading and writing this synchronously, before the
 * first `await`, is what makes "a run-now fires once" a property rather than a
 * race the tests happen to win.
 *
 * THE TYPE NOW LIVES IN lib/gate.ts and is re-exported here for this module's
 * existing consumers. That module is where the whole argument, and the
 * controller's two-tier ruling (final review I-2), is written down — every
 * other view reaches the same mechanism through its `guardedAction`. The two
 * orchestrations below keep taking the gate INLINE rather than through that
 * helper for one reason: `spec.confirm` is consulted between the check and the
 * send, so point C above ("a declined confirmation sends nothing") stays inside
 * the function its own tests drive.
 */
export type { ActionGate };

export interface RunActionIO {
  gate: ActionGate;
  queryClient: QueryClient;
  runAction: (action: string) => Promise<AdminActionResponse>;
  /** `window.confirm` at the call site; a collaborator here so the ASKING is
   *  part of the action rather than something a call site may forget. */
  confirm: (message: string) => boolean;
  setPending: (name: ActionName | null) => void;
  setOutcome: (outcome: ActionOutcome | null) => void;
}

/**
 * One admin action, applied. Returns `null` when nothing was sent — a declined
 * confirmation or a re-entrant click — and an `ActionOutcome` otherwise.
 *
 * INVALIDATION FOLLOWS THE ROW, not the outcome (lib/startView.ts's
 * `triggerRefreshCache`, same rule): the server writes the jobs row BEFORE it
 * responds in both modes, so a resolved response means the monitor and the
 * Start view's "letzte Jobs" are stale whether the job body succeeded or not.
 * A thrown ApiError (409/503/5xx) created no row and refreshes nothing. It goes
 * through `invalidateMutation`, never a hand-written `invalidateQueries`, so
 * queries.ts's map stays the one place that answers "what does this refresh?".
 */
export async function runAdminAction(name: ActionName, io: RunActionIO): Promise<ActionOutcome | null> {
  if (io.gate.current !== null) return null;
  const spec = ADMIN_ACTIONS[name];
  if (spec.confirm && !io.confirm(actionConfirmText(name))) return null;

  io.gate.current = name;
  io.setPending(name);
  io.setOutcome(null);
  let outcome: ActionOutcome;
  try {
    const response = await io.runAction(name);
    invalidateMutation(io.queryClient, 'runAction');
    outcome = classifyActionResponse(name, response);
  } catch (error) {
    // A 401 drives the SESSION, not just this panel's outcome line (final
    // review I-1). `classifyActionFailure` still runs, so the operator gets
    // `error.unauthenticated` in the outcome slot while the shell above comes
    // down -- the message and the screen agree instead of contradicting.
    reportAuthFailure(io.queryClient, error);
    outcome = classifyActionFailure(name, error);
  } finally {
    io.gate.current = null;
    io.setPending(null);
  }
  io.setOutcome(outcome);
  return outcome;
}

// ── the monitor ──────────────────────────────────────────────────────────────

/** The monitor row's DOM id, so a `409 job_running` can scroll to the run it
 *  collided with rather than to "a row". */
export function rowDomId(jobId: string): string {
  return `job-row-${jobId}`;
}

export interface FocusJobIO {
  find: (domId: string) => { scrollIntoView?: (options?: ScrollIntoViewOptions) => void } | null;
}

/**
 * Scrolls to the job the SERVER named. `jobId` comes from the 409's own body
 * (adminRoutes.ts:1608), never from "the first running row on screen" -- the
 * collided run may not be the newest one, and scrolling to the wrong row tells
 * the operator a different job is the blocker than the one that is.
 */
export function focusJob(jobId: string | null, io: FocusJobIO): boolean {
  // Truthiness, not `=== null`: `undefined` is reachable through api.ts's
  // unchecked `body as T` cast, and `rowDomId(undefined)` would look for a row
  // called `job-row-undefined` instead of refusing.
  if (!jobId) return false;
  const element = io.find(rowDomId(jobId));
  if (!element || typeof element.scrollIntoView !== 'function') return false;
  element.scrollIntoView({ block: 'center' });
  return true;
}

// ── DATEV: the one action that returns a file ────────────────────────────────

export const DATEV_PREVIEW_ACTION = 'datev-export-preview';

/** The server's own shape check (adminRoutes.ts:1506). Transcribed rather than
 *  loosened: the time part is MANDATORY, and the value also supplies the
 *  Content-Disposition filename server-side. */
export const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/;

/** What `<input type="datetime-local">` produces: local wall time, seconds
 *  optional, never an offset. */
const DATETIME_LOCAL = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

/**
 * A `datetime-local` value -> the UTC instant the server will accept.
 *
 * THE CONVERSION IS THE POINT. The operator types a LOCAL wall time and the
 * server's filter is in UTC, so sending the string through unchanged would move
 * the export window by the tenant's offset -- silently, and by two hours in
 * German summer.
 *
 * THE ROUND-TRIP IS ALSO THE POINT, and it is not defensive decoration: neither
 * `Date.parse` nor the `Date` constructor rejects an impossible date. V8 reads
 * `2026-02-30T13:00` as 2 March, and `13:99` as 14:39 -- so a naive conversion
 * would answer a plausible ISO instant for a date the operator never asked for
 * and quietly export the wrong period. The components are therefore read back
 * out of the constructed Date, and a value that did not survive is refused
 * before a request leaves.
 *
 * The HOUR is deliberately not round-tripped, only the calendar date: on a
 * spring-forward Sunday the local time 02:30 does not exist and the platform
 * shifts it to 03:30, which is the correct instant for what was typed. Hour and
 * minute are range-checked numerically instead, which is what catches `13:99`.
 */
export function toIsoInstant(local: string): string | null {
  const match = DATETIME_LOCAL.exec(local);
  if (!match) return null;
  const [year, month, day, hour, minute, second] =
    match.slice(1).map((part) => Number(part ?? '0'));
  if (year === undefined || month === undefined || day === undefined) return null;
  if (hour === undefined || minute === undefined) return null;
  if (hour > 23 || minute > 59 || (second ?? 0) > 59) return null;
  const date = new Date(year, month - 1, day, hour, minute, second ?? 0);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  const iso = date.toISOString();
  return ISO_INSTANT.test(iso) ? iso : null;
}

export const DATEV_FALLBACK_FILENAME = 'datev-export.txt';

/**
 * The server's `Content-Disposition` filename, which is the name the file must
 * be saved under (the DATEV Bankenimport expects it). Path separators are
 * replaced and leading dots stripped before the value reaches a `download`
 * attribute: the server already refuses a quote, a colon and a CRLF by
 * validating the range up front, and this is the client half of the same care.
 */
export function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  const quoted = /filename\s*=\s*"([^"]*)"/i.exec(header);
  const bare = /filename\s*=\s*([^;]+)/i.exec(header);
  const raw = (quoted?.[1] ?? bare?.[1] ?? '').trim();
  const safe = raw.replace(/[\\/]/g, '_').replace(/^\.+/, '').trim();
  return safe === '' ? null : safe;
}

export type DatevOutcome =
  | { kind: 'file'; filename: string; rowCount: number | null }
  /** The two fields, refused before a request leaves — or refused by the
   *  server's own shape check, which is the same statement. */
  | { kind: 'invalidRange' }
  /** 400 datev_export_failed: the ten `datev_*` keys. The operator fixes it. */
  | { kind: 'configMissing'; message: string }
  /** 502: a transient upstream failure. A retry is the right offer. */
  | { kind: 'upstream'; message: string }
  /** 503 feature_disabled: eBay is off, and this panel should not have rendered. */
  | { kind: 'featureDisabled' }
  | { kind: 'failed'; message: string };

export function datevOutcomeTone(outcome: DatevOutcome): OutcomeTone {
  switch (outcome.kind) {
    case 'file': return 'ok';
    case 'featureDisabled': return 'bug';
    case 'invalidRange':
    case 'configMissing':
    case 'upstream':
    case 'failed':
      return 'error';
  }
}

export function datevOutcomeMessage(outcome: DatevOutcome): string {
  switch (outcome.kind) {
    case 'file':
      return outcome.rowCount === null
        ? t('jobs.datev.doneNoCount', { file: outcome.filename })
        : t('jobs.datev.done', { file: outcome.filename, rows: outcome.rowCount });
    case 'invalidRange': return t('jobs.datev.invalidRange');
    case 'configMissing': return t('jobs.datev.configMissing');
    case 'upstream': return t('jobs.datev.upstream');
    case 'featureDisabled': return t('jobs.datev.featureDisabledBug');
    case 'failed': return outcome.message;
  }
}

/**
 * `datevPreview` uses `adminFetchRaw`, so a non-2xx comes back as a RESPONSE,
 * not as an `ApiError` -- deliberately (Task 3 review, forward-carried): the
 * success path is a file, and routing it through `adminFetch` would have tried
 * to JSON-parse a windows-1252 text body. The taxonomy in lib/api.ts therefore
 * does not cover this route and the classification is manual.
 *
 * THE 400/502 SPLIT IS NOT COSMETIC. The merged handler
 * (adminRoutes.ts:1548-1560) answers the SAME code `datev_export_failed` with
 * 400 when the tenant's DATEV configuration is missing and 502 when eBay or
 * Sheets failed underneath. Merging them back would send the operator to check
 * settings after an eBay outage, or tell them to retry a config that will never
 * work. The two SHAPE refusals (`since_and_until_required`,
 * `invalid_since_until`) are also 400s and are a third thing again -- the
 * caller's own input -- so the split is by CODE first, status second.
 */
export async function classifyDatevFailure(response: Response): Promise<DatevOutcome> {
  let body: Record<string, unknown> = {};
  try {
    body = (await response.json()) as Record<string, unknown>;
  } catch {
    body = {};
  }
  const code = readString(body.error);
  const message = typeof body.message === 'string' ? body.message.slice(0, 300) : '';
  // Checked on the STATUS, before any code lookup: a 401 from this route
  // carries the admin API's `{ error: 'unauthenticated' }` body, but it is the
  // one failure whose meaning does not depend on the body arriving at all
  // (final review I-1, the same ordering components/ErrorNotice.tsx uses).
  if (response.status === 401) return { kind: 'failed', message: t('error.unauthenticated') };
  if (code === 'feature_disabled') return { kind: 'featureDisabled' };
  if (code === 'since_and_until_required' || code === 'invalid_since_until') return { kind: 'invalidRange' };
  if (code === 'datev_export_failed') {
    return response.status === 400 ? { kind: 'configMissing', message } : { kind: 'upstream', message };
  }
  if (response.status === 502) return { kind: 'upstream', message };
  return { kind: 'failed', message: message === '' ? t('error.generic') : message };
}

export interface DatevIO {
  /** Its OWN gate, not the actions panel's: the server deliberately keeps this
   *  export outside the per-tenant job lock (adminRoutes.ts:1586-1590), so a
   *  running sync must not disable the download. */
  gate: ActionGate;
  /** Only for the 401 report (I-1). This action invalidates nothing on
   *  success -- it creates no job row and changes no server state. */
  queryClient: QueryClient;
  datevPreview: (since: string, until: string) => Promise<Response>;
  /** The browser side effect, injected so the whole path is Node-testable. */
  download: (blob: Blob, filename: string) => void;
  setPending: (pending: boolean) => void;
  setOutcome: (outcome: DatevOutcome | null) => void;
}

export async function runDatevExport(
  sinceLocal: string,
  untilLocal: string,
  io: DatevIO,
): Promise<DatevOutcome | null> {
  if (io.gate.current !== null) return null;
  const since = toIsoInstant(sinceLocal);
  const until = toIsoInstant(untilLocal);
  // AN IMPOSSIBLE PERIOD, not just an impossible instant (Task 13 review m1).
  // `toIsoInstant` closes "30 February"; nothing -- client OR server -- closed
  // "until before since". `runDatevPreview` only runs ISO_INSTANT over each
  // value separately, so a transposed pair is a well-formed request for an
  // inverted range and comes back as a cheerful "…wurde heruntergeladen
  // (0 Zeilen)." That is the same quiet wrongness as the date rollover, one
  // level up: a period the operator never asked for, reported as a success.
  // ISO-8601 UTC instants of equal precision compare lexicographically, and
  // `toIsoInstant` always emits the same `…THH:MM:SS.sssZ` shape.
  if (since === null || until === null || since >= until) {
    const refused: DatevOutcome = { kind: 'invalidRange' };
    io.setOutcome(refused);
    return refused;
  }

  io.gate.current = DATEV_PREVIEW_ACTION;
  io.setPending(true);
  io.setOutcome(null);
  let outcome: DatevOutcome;
  try {
    const response = await io.datevPreview(since, until);
    if (response.ok) {
      const blob = await response.blob();
      const filename = parseContentDispositionFilename(response.headers.get('Content-Disposition'))
        ?? DATEV_FALLBACK_FILENAME;
      const rowCount = readNumber(response.headers.get('X-Datev-Row-Count'));
      // The file is handed over BEFORE the outcome is reported: a confirmation
      // line naming a row count the operator never received is a lie.
      io.download(blob, filename);
      outcome = { kind: 'file', filename, rowCount };
    } else {
      // BY STATUS, not by ApiError: `adminFetchRaw` hands this route a plain
      // Response, so lib/api.ts's taxonomy never classifies it and
      // `reportAuthFailure` has nothing to read (final review I-1).
      reportAuthStatus(io.queryClient, response.status);
      outcome = await classifyDatevFailure(response);
    }
  } catch (error) {
    reportAuthFailure(io.queryClient, error);
    outcome = { kind: 'failed', message: errorMessage(error) };
  } finally {
    io.gate.current = null;
    io.setPending(false);
  }
  io.setOutcome(outcome);
  return outcome;
}

/**
 * The real download. An `<a download>` plus an object URL is the only way a
 * fetched body reaches the disk, and the URL is revoked on the NEXT turn rather
 * than in this one -- revoking it synchronously after `click()` has been known
 * to cancel the download in WebKit.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

// ── the metrics panel's four states ──────────────────────────────────────────

export const METRIC_RANGES: readonly MetricRange[] = ['24h', '7d', '30d'] as const;

export function metricRangeLabel(range: MetricRange): string {
  switch (range) {
    case '24h': return t('jobs.metrics.range.24h');
    case '7d': return t('jobs.metrics.range.7d');
    case '30d': return t('jobs.metrics.range.30d');
  }
}

export type MetricsState =
  | { kind: 'loading' }
  /** 503 metrics_unconfigured: THE PANEL ALONE says so (spec 4.7, risk A3). */
  | { kind: 'unconfigured' }
  | { kind: 'error'; message: string }
  /** Every series present and empty. On a fresh environment this is correct
   *  and not a fault (3a runbook 4). */
  | { kind: 'empty'; bundle: MetricsBundle }
  | { kind: 'ready'; bundle: MetricsBundle; partial: boolean };

/**
 * IS THIS BUNDLE INCOMPLETE? The server's `partial` flag OR any series that is
 * not an array. Both, not just the flag: `series` reaches this client through
 * api.ts's unchecked `body as T` cast, so "the flag says complete but a series
 * is null" is a reachable disagreement, and the honest reading of a
 * disagreement is the pessimistic one. A partial bundle must never look
 * complete.
 */
export function bundleIsPartial(bundle: MetricsBundle): boolean {
  return bundle.partial === true || METRIC_SERIES.some((name) => seriesRowsOf(bundle, name) === null);
}

/**
 * "noch keine Daten in diesem Zeitraum" REQUIRES A COMPLETE BUNDLE. A partial
 * one whose surviving series happen to be empty is not an empty environment --
 * it is an environment we could not fully ask, and reporting it as "no data
 * yet" is precisely the misreport this view exists to avoid: a stalled system
 * reads as a quiet one.
 */
export function metricsViewState(query: {
  isPending: boolean;
  isError: boolean;
  error: unknown;
  data: MetricsBundle | undefined;
}): MetricsState {
  if (query.isPending) return { kind: 'loading' };
  if (query.isError) {
    if (query.error instanceof ApiError && query.error.code === 'metrics_unconfigured') {
      return { kind: 'unconfigured' };
    }
    return { kind: 'error', message: errorMessage(query.error) };
  }
  const bundle = query.data;
  if (bundle === undefined) return { kind: 'error', message: t('error.generic') };
  const partial = bundleIsPartial(bundle);
  if (!partial && METRIC_SERIES.every((name) => seriesRowsOf(bundle, name)?.length === 0)) {
    return { kind: 'empty', bundle };
  }
  return { kind: 'ready', bundle, partial };
}
